import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookEditor } from '@/components/publisher/BookEditor';
import { Sidebar } from '@/components/publisher/Sidebar';
import { ChapterEditView } from '@/components/publisher/ChapterEditView';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { PublisherHeader } from '@/components/publisher/PublisherHeader';
import { useBookEditor } from '@/hooks/useBookEditor';
import { useChapterEditor } from '@/hooks/useChapterEditor';
import type { ChapterUpdate } from '@/types';

export function Publisher() {
  const navigate = useNavigate();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Custom hooks
  const bookEditor = useBookEditor();
  const chapterEditor = useChapterEditor();
  
  // Local UI state
  const [sidebarTab, setSidebarTab] = useState<'preview' | 'audio'>('preview');

  // Derived state
  const canPublish = !!(bookEditor.title.trim() && bookEditor.author.trim() && bookEditor.markdown.trim());

  // Handlers
  const handleCancel = () => {
    navigate('/admin/books');
  };

  const insertAudioMarker = useCallback((audioId: string, label: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const cursorPos = textarea.selectionStart;
    const before = bookEditor.markdown.slice(0, cursorPos);
    const after = bookEditor.markdown.slice(cursorPos);
    const marker = `\n[audio:${audioId}|${label}]\n`;
    bookEditor.setMarkdown(before + marker + after);
    setTimeout(() => {
      textarea.focus();
      const newPos = cursorPos + marker.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  }, [bookEditor]);

  const handleChapterClick = useCallback((chapterIndex: number) => {
    chapterEditor.handleChapterClick(chapterIndex, bookEditor.markdown, bookEditor.preview);
  }, [chapterEditor, bookEditor]);

  const handleChapterSave = useCallback(async (updates: ChapterUpdate) => {
    // Update the token cost in the book editor
    bookEditor.updateChapterTokenCost(chapterEditor.editingChapter?.sequence || 0, updates.tokenCost);
    
    await chapterEditor.handleChapterSave(
      updates,
      bookEditor.markdown,
      bookEditor.setMarkdown,
      (preview) => bookEditor.setPreview(preview)
    );
  }, [chapterEditor, bookEditor]);

  const handlePrevChapter = useCallback(() => {
    if (chapterEditor.editingChapterIndex === null || chapterEditor.editingChapterIndex <= 0) return;
    handleChapterClick(chapterEditor.editingChapterIndex - 1);
  }, [chapterEditor.editingChapterIndex, handleChapterClick]);

  const handleNextChapter = useCallback(() => {
    if (
      chapterEditor.editingChapterIndex === null || 
      !bookEditor.preview || 
      chapterEditor.editingChapterIndex >= bookEditor.preview.chapters.length - 1
    ) return;
    handleChapterClick(chapterEditor.editingChapterIndex + 1);
  }, [chapterEditor.editingChapterIndex, bookEditor.preview, handleChapterClick]);

  return (
    <AdminLayout>
      <div className="h-[calc(100vh-64px)] overflow-hidden">
        <PublisherHeader
          isLoadingBook={bookEditor.isLoadingBook}
          isEditMode={bookEditor.isEditMode}
          hasUnsavedChanges={bookEditor.hasUnsavedChanges}
          isPublishing={bookEditor.isPublishing}
          canPublish={canPublish}
          onCancel={handleCancel}
          onPublish={bookEditor.handlePublish}
        />
        
        <div className="h-[calc(100vh-140px)] overflow-hidden">
          <div className="h-full grid grid-cols-1 lg:grid-cols-12">
            <div className="lg:col-span-8 h-full overflow-auto">
              <div className="p-6 h-full">
                {chapterEditor.editingChapter ? (
                  <ChapterEditView
                    key={chapterEditor.editingChapter._id}
                    chapter={chapterEditor.editingChapter}
                    totalChapters={bookEditor.preview?.chapters.length || 1}
                    onSave={handleChapterSave}
                    onCancel={chapterEditor.handleChapterCancel}
                    onPrevChapter={handlePrevChapter}
                    onNextChapter={handleNextChapter}
                  />
                ) : (
                  <BookEditor
                    title={bookEditor.title}
                    author={bookEditor.author}
                    blurb={bookEditor.blurb}
                    markdown={bookEditor.markdown}
                    coverUrl={bookEditor.coverUrl}
                    heroCoverUrl={bookEditor.heroCoverUrl}
                    error={bookEditor.error}
                    publishResult={bookEditor.publishResult}
                    isPublishing={bookEditor.isPublishing}
                    isEditMode={bookEditor.isEditMode}
                    onTitleChange={bookEditor.setTitle}
                    onAuthorChange={bookEditor.setAuthor}
                    onBlurbChange={bookEditor.setBlurb}
                    onMarkdownChange={bookEditor.setMarkdown}
                    onCoverUrlChange={bookEditor.setCoverUrl}
                    onHeroCoverUrlChange={bookEditor.setHeroCoverUrl}
                    onLoadFromFile={bookEditor.loadFromFile}
                    onPublish={bookEditor.handlePublish}
                    textareaRef={textareaRef}
                  />
                )}
              </div>
            </div>

            <div className="lg:col-span-4 h-full border-l border-[#e7e5e4] bg-[#fafaf9] overflow-auto">
              <Sidebar
                activeTab={sidebarTab}
                preview={bookEditor.preview}
                onTabChange={setSidebarTab}
                onChapterClick={handleChapterClick}
                onChapterEdit={(chapter) => {
                  // Find the chapter index
                  const index = bookEditor.preview?.chapters.findIndex(
                    (ch) => ch.sequence === chapter.sequence
                  );
                  if (index !== undefined && index >= 0) {
                    handleChapterClick(index);
                  }
                }}
                onInsertAudio={insertAudioMarker}
              />
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
